import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axios";
import {
  getFoodRequests,
  getValetRequests,
  getBaggageRequests,
  getSOSRequests,
  getElderlyAssistRequests,
} from "../../utils/api";
import "./styles.css";


const AdminDashboard = () => {
  const [selectedEstablishment, setSelectedEstablishment] = useState("all");
  const [establishments, setEstablishments] = useState([]);

  const dashboardData = {
    pendingRequests: 12,
    approvedToday: 8,
    rejectedToday: 3,
    todaysOrders: 24,
    pendingFulfillment: 5,
    completedOrders: 19,
    availableStaff: 58,
    staffOnDuty: 42,
    staffStandby: 16,
    staffOnLeave: 7,
    sickLeave: 4,
    plannedLeave: 3,
  };

  const statusColors = {
    pending: "status-pending",
    approved: "status-approved",
    rejected: "status-rejected",
  };

  const [foodRequests, setFoodRequests] = useState([]);
  const [valetRequests, setValetRequests] = useState([]);
  const [baggageRequests, setBaggageRequests] = useState([]);
  const [sosRequests, setSosRequests] = useState([]);
  const [elderlyRequests, setElderlyRequests] = useState([]);

  const [modalTitle, setModalTitle] = useState("");
  const [modalData, setModalData] = useState([]);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    axiosInstance
      .post("/view_establishment/v1.1", { view_type: "view" })
      .then((res) => {
        if (res.data?.Status === "Success") {
          const apiEstablishments = Object.entries(res.data.Data).map(
            ([id, name]) => ({ id, name })
          );
          setEstablishments([
            { id: "all", name: "All Establishments" },
            ...apiEstablishments,
          ]);
        }
      })
      .catch((err) => {
        console.error("Error fetching establishments:", err);
      });
  }, []);

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const [food, valet, baggage, sos, elderly] = await Promise.all([
          getFoodRequests(1),
          getValetRequests(),
          getBaggageRequests(),
          getSOSRequests(),
          getElderlyAssistRequests(),
        ]);
        setFoodRequests(food?.data?.Data || []);
        setValetRequests(valet || []);
        setBaggageRequests(baggage || []);
        setSosRequests(sos || []);
        setElderlyRequests(elderly || []);
      } catch (error) {
        console.error("Error fetching VAS requests", error);
      }
    };

    fetchRequests();
  }, []);

  const handleCardClick = (title, data) => {
    setModalTitle(title);
    setModalData(data);
    setShowModal(true);
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return "";
    const now = new Date();
    const then = new Date(timestamp);
    const diff = now - then;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const extractLatest = (title, data) => {
    const latest = data?.[0];
    if (!latest) return null;
    return {
      id: latest._id || latest.id || title,
      name: title,
      type: latest.request_type || "Request",
      status: latest.status || "pending",
      time: formatTimeAgo(latest.transaction_time),
    };
  };

  const recentRequests = [
    extractLatest("Food Delivery", foodRequests),
    extractLatest("Valet Service", valetRequests),
    extractLatest("Baggage Assistance", baggageRequests),
    extractLatest("SOS Alert", sosRequests),
    extractLatest("Elderly Assistance", elderlyRequests),
  ].filter(Boolean);

  return (
    <div className="admin-dashboard">
      <header className="dashboard-header">
        <h2>Administrator Dashboard</h2>
        <div className="establishment-selector">
          <select
            value={selectedEstablishment}
            onChange={(e) => setSelectedEstablishment(e.target.value)}
          >
            {establishments.map((est) => (
              <option key={est.id} value={est.id}>
                {est.name}
              </option>
            ))}
          </select>
          <span className="select-arrow">▼</span>
        </div>
      </header>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="card-header">
            <h3>Pending Requests</h3>
            <div className="icon-container request-icon">📝</div>
          </div>
          <div className="card-main-value">{dashboardData.pendingRequests}</div>
          <div className="card-footer">
            <span>Approved: {dashboardData.approvedToday}</span>
            <span>Pending: {dashboardData.rejectedToday}</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="card-header">
            <h3>Today's Orders</h3>
            <div className="icon-container order-icon">🛒</div>
          </div>
          <div className="card-main-value">{dashboardData.todaysOrders}</div>
          <div className="card-footer">
            <span>Pending: {dashboardData.pendingFulfillment}</span>
            <span>Completed: {dashboardData.completedOrders}</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="card-header">
            <h3>Available Staff</h3>
            <div className="icon-container staff-icon">👥</div>
          </div>
          <div className="card-main-value">{dashboardData.availableStaff}</div>
          <div className="card-footer">
            <span>On duty: {dashboardData.staffOnDuty}</span>
            <span>Standby: {dashboardData.staffStandby}</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="card-header">
            <h3>Staff on Leave</h3>
            <div className="icon-container leave-icon">🏖️</div>
          </div>
          <div className="card-main-value">{dashboardData.staffOnLeave}</div>
          <div className="card-footer">
            <span>Sick: {dashboardData.sickLeave}</span>
            <span>Planned: {dashboardData.plannedLeave}</span>
          </div>
        </div>
      </div>

      <div className="recent-activity">
        <h2>Recent Requests</h2>
        <div className="requests-list">
          {recentRequests.map((request) => (
            <div key={request.id} className="request-item">
              <div className="request-info">
                <div className="request-name">{request.name}</div>
                <div className="request-type">{request.type}</div>
              </div>
              <div className={`request-status ${statusColors[request.status]}`}>
                {request.status} {request.time && `• ${request.time}`}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="vas-section">
        <h2>Service Requests</h2>
        <div className="vas-grid">
          {[
            { title: "Food Requests", data: foodRequests },
            { title: "Valet Requests", data: valetRequests },
            { title: "Baggage Requests", data: baggageRequests },
            { title: "SOS Alerts", data: sosRequests },
            { title: "Elderly Assistance", data: elderlyRequests },
          ].map((item) => (
            <div
              className="vas-card"
              key={item.title}
              onClick={() => handleCardClick(item.title, item.data)}
            >
              <div className="card-header">
                <h3>{item.title}</h3>
                <div className="icon-container">📦</div>
              </div>
              <div className="card-main-value">{item.data.length}</div>
              <div className="card-footer">Click to view details</div>
            </div>
          ))}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay-ad" onClick={() => setShowModal(false)}>
          <div className="modal-content-ad" onClick={(e) => e.stopPropagation()}>
            <h3>{modalTitle}</h3>
            <button
              className="modal-close-ad"
              onClick={() => setShowModal(false)}
            >
              &times;
            </button>
            <ul className="modal-list-ad">
              {modalData.length === 0 ? (
                <p>No data available.</p>
              ) : (
                modalData.map((item, idx) => (
                  <li key={idx} className="modal-item-ad">
                    <strong>
                      {item.first_name} {item.last_name}
                    </strong>{" "}
                    - {item.user_contact}
                    <br />
                    <small>{item.transaction_time}</small>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
